import React from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowRight, Check, ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import QuoteForm from "@/components/QuoteForm";
import { SERVICE_PAGES } from "@/seo-pages.config";

export default function ServiceDetailPage({ slug }) {
  const page = SERVICE_PAGES.find((p) => p.slug === slug);

  if (!page) return <Navigate to="/services" replace />;

  const Icon = page.icon;
  const related = (page.relatedSlugs || [])
    .map((s) => SERVICE_PAGES.find((p) => p.slug === s))
    .filter(Boolean);

  return (
    <main data-testid={`seo-page-${page.slug}`} className="pt-32">
      <Seo
        title={page.seo.title}
        description={page.seo.description}
        keywords={page.seo.keywords}
        canonical={`https://signaclean.fr/${page.slug}`}
      />

      {/* Hero */}
      <section className="relative px-5 sm:px-8 pb-16">
        <div className="absolute inset-0 -z-10 bg-radial-fade" />
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#9DEB30]/30 bg-[#9DEB30]/5 mb-5">
              <Icon size={14} className="text-[#9DEB30]" />
              <span className="text-xs font-mono uppercase tracking-[0.18em] text-[#9DEB30]">
                Service · Martinique
              </span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight">
              {page.h1}
            </h1>
            <p className="mt-6 text-lg text-zinc-300 max-w-xl">{page.intro}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/contact"
                data-testid={`seo-cta-primary-${page.slug}`}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full btn-neon text-sm"
              >
                {page.cta}
                <ArrowRight size={16} />
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full btn-ghost-neon text-sm"
              >
                Tous nos services
              </Link>
            </div>
          </div>
          <div className="relative aspect-[4/3] rounded-2xl overflow-hidden border border-white/10">
            <img
              src={page.image}
              alt={page.h1}
              style={{ objectPosition: page.imagePosition || "center" }}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-black/70 via-black/20 to-transparent" />
          </div>
        </div>
      </section>

      {/* Sections */}
      <section className="px-5 sm:px-8 py-12 bg-[#0c0c0c] border-y border-white/5">
        <div className="max-w-5xl mx-auto space-y-12">
          {page.sections.map((section, i) => (
            <article key={i} data-testid={`seo-section-${i}`}>
              <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight mb-4">
                <span className="text-[#9DEB30]">0{i + 1}.</span> {section.title}
              </h2>
              {section.body && (
                <p className="text-zinc-300 leading-relaxed max-w-3xl">{section.body}</p>
              )}
              {section.bullets && (
                <ul className="mt-3 space-y-2 max-w-3xl">
                  {section.bullets.map((b, j) => (
                    <li key={j} className="flex items-start gap-2 text-zinc-200">
                      <Check size={16} className="text-[#9DEB30] mt-1 shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}
            </article>
          ))}
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="px-5 sm:px-8 py-16">
          <div className="max-w-7xl mx-auto">
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#9DEB30] mb-3">
              · Voir aussi
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mb-8">
              Nos autres prestations en Martinique
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {related.map((r) => {
                const RIcon = r.icon;
                return (
                  <Link
                    key={r.slug}
                    to={`/${r.slug}`}
                    data-testid={`related-${r.slug}`}
                    className="group p-6 rounded-2xl border border-white/10 hover:border-[#9DEB30]/40 bg-[#101010] transition flex items-start gap-4"
                  >
                    <div className="h-11 w-11 rounded-xl flex items-center justify-center bg-[#9DEB30]/10 border border-[#9DEB30]/30 text-[#9DEB30]">
                      <RIcon size={18} />
                    </div>
                    <div className="flex-1">
                      <div className="font-display text-lg font-bold leading-snug">
                        {r.h1}
                      </div>
                      <div className="mt-2 text-sm text-zinc-400 inline-flex items-center gap-1">
                        Découvrir <ArrowUpRight size={14} />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Contact form */}
      <QuoteForm />
    </main>
  );
}
